import {
  registerIntegrationLabeller,
  startCased,
} from "../types/integration-label.ts";
import type { EmptyAccesses } from "../types/lifecycle.ts";
import type { RegisteredIntegrationKey } from "./registry-keys.ts";
import { sealGuard } from "./seal-guard.ts";
import { type RegisteredResourceIntegration } from "./types.ts";

export const CloudflareIconUrl = "https://www.cloudflare.com/favicon.ico";
export const JiraIconUrl =
  "https://wac-cdn.atlassian.com/dam/jcr:fa01756d-6dcc-45d1-83ab-696fbfeb074f/Jira-icon-blue.svg?cdnVersion=1571";
export const PingIconUrl =
  "https://www.pingidentity.com/etc.clientlibs/settings/wcm/designs/pic6/assets/resources/images/favicon.ico";

/** Hides "Components" and "Accesses" complexity from consumers of the registry */
const mask = (registration: RegisteredResourceIntegration) => registration;

const registrations = {} as Record<
  RegisteredIntegrationKey,
  RegisteredResourceIntegration
>;

/** Adds one integration to the registry.
 *
 * Called at module scope by each integration's own `registry.ts`, so that
 * importing an integration is what puts it in the registry. Nothing forces a
 * composition root to import them all — see `./registrations.ts`, and
 * {@link sealGuard} for what catches it when one doesn't.
 */
export const registerIntegration = (
  key: RegisteredIntegrationKey,
  registration: RegisteredResourceIntegration
): void => {
  registrations[key] = mask(registration);
};

/** For now just directory and resource integrations */
export const integrationRegistry: Record<
  RegisteredIntegrationKey,
  RegisteredResourceIntegration
> = sealGuard(registrations);

registerIntegrationLabeller((integration) => integrationLabel(integration));

export const integrationLabel = (integration: string): string =>
  (integrationRegistry as Record<string, RegisteredResourceIntegration>)[
    integration
  ]?.label ?? startCased(integration);

/** Resolves a resource integration's static Accesses registry at runtime from its type key.
 *
 * Used when the delegate is not known at compile time, e.g. access bundles.
 */
export const integrationAccesses = (integration: string): EmptyAccesses =>
  (integrationRegistry as Record<string, RegisteredResourceIntegration>)[
    integration
  ]?.accesses ?? {};
